import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { keys } from "@/lib/queries";
import type { CryptoDepositRow, CryptoOptions, CryptoWithdrawalRow, PageInfo } from "@/lib/types";
import { Tabs } from "@/components/ui";
import { CryptoDepositPanel } from "@/components/wallet/crypto/CryptoDepositPanel";
import { CryptoWithdrawPanel } from "@/components/wallet/crypto/CryptoWithdrawPanel";
import { buildHead } from "@/lib/seo";

// Dev preview: seeds the query cache with mock crypto-rail data so the
// /wallet/crypto panels (SM-212) render without a backend.
export const Route = createFileRoute("/wallet-crypto-preview")({
  head: () =>
    buildHead({ path: "/wallet-crypto-preview", title: "Crypto wallet preview (dev)", noindex: true }),
  component: Preview,
});

const options: CryptoOptions = {
  deposit_enabled: true,
  withdraw_enabled: true,
  networks: [
    {
      network_id: "eth-mainnet",
      name: "Ethereum",
      chain_id: 1,
      confirmations: 12,
      assets: [
        {
          asset_id: "eth",
          symbol: "ETH",
          kind: "NATIVE",
          deposit_enabled: true,
          withdrawal_enabled: true,
          token_decimals: 18,
          min_deposit_base: "5000000000000000", // 0.005 ETH
          rate: { rate_num: 200_000, rate_den: 1, spark_minor_per_unit: 100 }, // 1 ETH = ⚡200,000
          withdrawal: { fee_mode: "GAS_PASSTHROUGH", fee_value: 0, min_base: "10000000000000000" },
        },
        {
          asset_id: "usdc-eth",
          symbol: "USDC",
          kind: "ERC20",
          deposit_enabled: true,
          withdrawal_enabled: true,
          token_contract: "0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48",
          token_decimals: 6,
          min_deposit_base: "5000000", // 5 USDC
          rate: { rate_num: 100, rate_den: 1, spark_minor_per_unit: 100 }, // 1 USDC = ⚡100
          withdrawal: { fee_mode: "FLAT", fee_value: "1000000", min_base: "10000000", max_base: "5000000000" },
        },
      ],
    },
    {
      network_id: "base",
      name: "Base",
      chain_id: 8453,
      confirmations: 30,
      assets: [
        {
          asset_id: "usdc-base",
          symbol: "USDC",
          kind: "ERC20",
          deposit_enabled: true,
          withdrawal_enabled: true,
          token_contract: "0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913",
          token_decimals: 6,
          min_deposit_base: "2000000",
          rate: { rate_num: 100, rate_den: 1, spark_minor_per_unit: 100 },
          withdrawal: { fee_mode: "PCT", fee_value: 50, min_base: "5000000" }, // 0.5%
        },
      ],
    },
    {
      // SM-277 visibility matrix — mirrors the SIT bug report.
      network_id: "bitkub_testnet",
      name: "Bitkub Testnet",
      chain_id: 25925,
      confirmations: 2,
      assets: [
        {
          // Withdrawal-enabled, admin never saved a policy → the server now
          // ships the zero-fee default block. Must appear in the selector.
          asset_id: "tkub",
          symbol: "tKUB",
          kind: "NATIVE",
          deposit_enabled: true,
          withdrawal_enabled: true,
          token_decimals: 18,
          rate: { rate_num: 100, rate_den: 1, spark_minor_per_unit: 100 }, // 1 tKUB = ⚡100
          withdrawal: { fee_mode: "FLAT", fee_value: 0 },
        },
        {
          // Withdrawal-enabled but NO pricing block (old-server skew) — the
          // flag alone must be enough to list it; fee renders "—".
          asset_id: "tbnb",
          symbol: "tBNB",
          kind: "ERC20",
          deposit_enabled: true,
          withdrawal_enabled: true,
          token_contract: "0x0000000000000000000000000000000000000bnb",
          token_decimals: 18,
          rate: { rate_num: 500, rate_den: 1, spark_minor_per_unit: 100 },
        },
        {
          // Deposit-only — must stay OUT of the withdraw selector.
          asset_id: "usdt-bkc",
          symbol: "USDT",
          kind: "ERC20",
          deposit_enabled: true,
          withdrawal_enabled: false,
          token_contract: "0x0000000000000000000000000000000000000usd",
          token_decimals: 6,
          rate: { rate_num: 100, rate_den: 1, spark_minor_per_unit: 100 },
        },
        {
          // Deposit-enabled but RATE-LESS — the deposit tab must show the
          // blocked notice instead of an address (credit would be withheld).
          asset_id: "ethk",
          symbol: "ETHK",
          kind: "ERC20",
          deposit_enabled: true,
          withdrawal_enabled: false,
          token_contract: "0x0000000000000000000000000000000000000e7h",
          token_decimals: 18,
        },
      ],
    },
  ],
};

const now = Date.now();
const hash = (i: number) => `0x${String(i).repeat(4).padStart(8, "0")}9c1a7b2f4e6d8a0c1b3d5f7a9c1e3b5d7f90${i}ab`;

// Every row carries the currency its credit lands in, because that is what
// the list draws each figure with (SM-518). d3 and d6 credited minutes ago so
// they sit inside the 15-minute success window and actually show a figure —
// the state the mark can be wrong in. They deliberately disagree with each
// other AND with the panel's own scope (GOLD, below): a landed deposit's unit
// is a fact about the row, not about the tab it is being listed under.
const deposits: CryptoDepositRow[] = [
  { deposit_order_id: "d1", network_id: "eth-mainnet", asset_id: "eth", deposit_address: "0x1111", tx_hash: hash(1), amount_base: "250000000000000000", credited_spark_minor: 0, credited_currency: "RED", status: "DETECTED", detected_at: new Date(now - 60_000).toISOString() },
  { deposit_order_id: "d2", network_id: "eth-mainnet", asset_id: "usdc-eth", deposit_address: "0x1111", tx_hash: hash(2), amount_base: "125000000", credited_spark_minor: 0, credited_currency: "GOLD", status: "CONFIRMING", detected_at: new Date(now - 480_000).toISOString() },
  { deposit_order_id: "d3", network_id: "eth-mainnet", asset_id: "eth", deposit_address: "0x1111", tx_hash: hash(3), amount_base: "1000000000000000000", credited_spark_minor: 20_000_000, credited_currency: "RED", status: "CREDITED", detected_at: new Date(now - 600_000).toISOString(), credited_at: new Date(now - 120_000).toISOString() },
  { deposit_order_id: "d6", network_id: "eth-mainnet", asset_id: "usdc-eth", deposit_address: "0x1111", tx_hash: hash(8), amount_base: "40000000", credited_spark_minor: 400_000, credited_currency: "GOLD", status: "CREDITED", detected_at: new Date(now - 500_000).toISOString(), credited_at: new Date(now - 90_000).toISOString() },
  { deposit_order_id: "d4", network_id: "base", asset_id: "usdc-base", deposit_address: "0x2222", tx_hash: hash(4), amount_base: "50000000", credited_spark_minor: 0, credited_currency: "GOLD", status: "CREDIT_FAILED", detected_at: new Date(now - 90_000_000).toISOString() },
  { deposit_order_id: "d5", network_id: "eth-mainnet", asset_id: "usdc-eth", deposit_address: "0x1111", tx_hash: hash(5), amount_base: "9000000", credited_spark_minor: 0, credited_currency: "GOLD", status: "REVERTED", detected_at: new Date(now - 100_000_000).toISOString() },
];

const withdrawals: CryptoWithdrawalRow[] = [
  { withdrawal_order_id: "w1", network_id: "eth-mainnet", asset_id: "usdc-eth", destination: "0xAbCdEf0123456789aBcDeF0123456789AbCdEf01", amount_base: "50000000", fee_base: "1000000", currency: "GOLD", debit_spark_minor: 500_000, status: "HELD", requested_at: new Date(now - 120_000).toISOString(), tx_hash: "" },
  { withdrawal_order_id: "w2", network_id: "eth-mainnet", asset_id: "eth", destination: "0xAbCdEf0123456789aBcDeF0123456789AbCdEf01", amount_base: "100000000000000000", fee_base: "0", currency: "RED", debit_spark_minor: 2_000_000, status: "BROADCAST", requested_at: new Date(now - 900_000).toISOString(), tx_hash: hash(6) },
  { withdrawal_order_id: "w3", network_id: "base", asset_id: "usdc-base", destination: "0xAbCdEf0123456789aBcDeF0123456789AbCdEf01", amount_base: "20000000", fee_base: "100000", currency: "GOLD", debit_spark_minor: 200_000, status: "COMPLETED", requested_at: new Date(now - 90_000_000).toISOString(), completed_at: new Date(now - 89_000_000).toISOString(), tx_hash: hash(7) },
  { withdrawal_order_id: "w4", network_id: "eth-mainnet", asset_id: "usdc-eth", destination: "0xAbCdEf0123456789aBcDeF0123456789AbCdEf01", amount_base: "15000000", fee_base: "1000000", currency: "GOLD", debit_spark_minor: 150_000, status: "SIGN_FAILED", requested_at: new Date(now - 20_000_000).toISOString(), tx_hash: "" },
  { withdrawal_order_id: "w5", network_id: "eth-mainnet", asset_id: "eth", destination: "0xAbCdEf0123456789aBcDeF0123456789AbCdEf01", amount_base: "5000000000000000000", fee_base: "0", currency: "GOLD", debit_spark_minor: 100_000_000, status: "REJECTED", requested_at: new Date(now - 260_000_000).toISOString(), tx_hash: "" },
];

const page = (total: number): PageInfo => ({ total, limit: 10, offset: 0, has_more: false });

function Preview() {
  const qc = useQueryClient();
  useState(() => {
    // Pin the seeds (walletCrypto() partial-matches every crypto key) so the
    // 10s history poll against the proxied (empty) backend can't clobber them.
    for (const k of [keys.me(), keys.walletBalance(), keys.walletCrypto()]) {
      qc.setQueryDefaults(k, { staleTime: Infinity, gcTime: Infinity, retry: false, refetchOnMount: false, refetchOnWindowFocus: false, refetchOnReconnect: false, refetchInterval: false });
    }
    qc.setQueryData(keys.me(), { user_id: "u1", email: "dev@local", role: "user" });
    qc.setQueryData(keys.walletBalance(), { available: "48250000", reserved: "5500000", pending_withdrawal: "0" });
    qc.setQueryData(keys.walletCryptoOptions("GOLD"), options);
    // Seeded per network AND currency (SM-504) — the preview's assets
    // all credit GOLD, so that is the destination it primes.
    qc.setQueryData(keys.walletCryptoDepositAddress("eth-mainnet", "GOLD"), { network_id: "eth-mainnet", currency: "GOLD", address: "0x8Ba1f109551bD432803012645Ac136ddd64DBA72" });
    qc.setQueryData(keys.walletCryptoDepositAddress("base", "GOLD"), { network_id: "base", currency: "GOLD", address: "0x71C7656EC7ab88b098defB751B7401B5f6d8976F" });
    qc.setQueryData(keys.walletCryptoDepositAddress("bitkub_testnet", "GOLD"), { network_id: "bitkub_testnet", currency: "GOLD", address: "0x9aE5100fA1cF3B9601Ca3cd6dCa56dAA2D1cB6a3" });
    qc.setQueryData(keys.walletCryptoDepositsPage(10, 0), { items: deposits, page: page(deposits.length) });
    qc.setQueryData(keys.walletCryptoWithdrawalsPage(10, 0), { items: withdrawals, page: page(withdrawals.length) });
    return true;
  });
  const [tab, setTab] = useState("deposit");
  return (
    <div className="mx-auto min-h-dvh max-w-2xl bg-bg p-6 text-fg">
      <h1 className="mb-4 font-display text-2xl font-bold">Crypto wallet preview (mock)</h1>
      <Tabs
        fullWidth
        className="mb-5"
        value={tab}
        onValueChange={setTab}
        items={[
          { value: "deposit", label: "Deposit" },
          { value: "withdraw", label: "Withdraw" },
        ]}
      />
      {tab === "withdraw" ? (
        <CryptoWithdrawPanel options={options} />
      ) : (
        <CryptoDepositPanel options={options} />
      )}
    </div>
  );
}
